import { ref } from 'vue'
import type { DispatchMetadata, ExperimentalUI, ExternalToolsPayload } from 'shared/src'
import {
  useConfigurator,
  parseRunToolsOnEnter,
  serializeAdditionalParams,
} from '@/composables/configurator/useConfigurator.ts'
import { useLiveConfigurationApi } from '@/composables/api/useLiveConfigurationApi.ts'
import { useQueryParameters } from '@/composables/url/useQueryParameters.ts'
import { serializeExternalTools } from '@/composables/configurator/externalToolsSerializer'
import { isNotBlank } from '@/utils/String'
import { GENERATION_MODEL_V2 } from '@/composables/configurator/useExperimentalOptions'
import { useAvailableVoices } from '@/composables/configurator/useAvailableVoices.ts'

export const useLiveConfigurator = () => {
  const loading = ref(false)
  const { assignLivePhoneNumber } = useLiveConfigurationApi()
  const { availableVoices } = useAvailableVoices()

  const resolveVoiceIds = (
    rawVoiceIds: Record<string, string> | undefined,
  ): Record<string, unknown> => {
    if (!rawVoiceIds) return {}
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return Object.fromEntries(
      Object.entries(rawVoiceIds).map(([lang, voiceId]) => {
        const voice = (availableVoices.value[lang] ?? []).find((v) => v.provider_id === voiceId)
        if (voice?.model_id) {
          return [lang, { model_id: voice.model_id, voice_id: voiceId }]
        }
        return [lang, voiceId]
      }),
    )
  }

  const hasVoiceWithCustomModel = (rawVoiceIds: Record<string, string> | undefined): boolean => {
    if (!rawVoiceIds) return false
    return Object.entries(rawVoiceIds).some(([lang, voiceId]) =>
      (availableVoices.value[lang] ?? []).some((v) => v.provider_id === voiceId && v.model_id),
    )
  }

  const getFullDispatchMetadata = (): DispatchMetadata => {
    const { payload, conversationFlow, experimental, externalTools } = useConfigurator()
    const experimentalConfig: Partial<ExperimentalUI> = experimental.value
    const defaultLang = payload.value.general_information.supported_languages.find(
      (lang) => lang.default,
    )
    const classIdentifierOverride = experimental.value.tools.custom_skill
      ? experimental.value.class_identifier_override
      : ''
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const defaultPersonaKey = experimental.value.tools?.step_validation
      ? 'validation_scenario'
      : 'qualification_scenario'
    const qualifier: string = isNotBlank(classIdentifierOverride)
      ? classIdentifierOverride.trim()
      : defaultPersonaKey
    const personaRole = experimental.value.tools.custom_skill
      ? experimentalConfig.persona_role_override
      : ''
    const personaRunToolsOnEnter = experimental.value.tools.custom_skill
      ? parseRunToolsOnEnter(experimentalConfig.persona_run_tools_on_enter ?? '')
      : undefined
    const personaVoiceIds = experimental.value.tools.custom_skill
      ? parseRunToolsOnEnter(experimentalConfig.persona_voice_ids_override ?? '')
      : undefined
    const personaUuid =
      isNotBlank(experimental.value.persona_uuid_override) &&
      (isNotBlank(personaRole) ||
        personaRunToolsOnEnter !== undefined ||
        personaVoiceIds !== undefined)
        ? experimental.value.persona_uuid_override
        : undefined

    const external_tools: ExternalToolsPayload | undefined = externalTools.value.servers.length
      ? serializeExternalTools(externalTools.value)
      : undefined

    const openingPhrases = conversationFlow.value.opening_phrases
      .map((item) => item.opening_phrase)
      .filter(isNotBlank)
    const closingPhrases = conversationFlow.value.closing_phrases
      .map((item) => item.closing_phrase)
      .filter(isNotBlank)

    const additionalParams = experimental.value.tools.custom_skill
      ? serializeAdditionalParams(experimentalConfig.additional_parameters ?? [])
      : undefined
    const contextualPrompt =
      experimental.value.tools.custom_skill &&
      isNotBlank(experimental.value.contextual_prompt_override)
        ? experimental.value.contextual_prompt_override
        : undefined

    return {
      scenario: {
        class_identifier: qualifier,
        fsm: payload.value.experimental?.fsm ?? false,
      },
      tools: {
        routing: null,
        common: payload.value.experimental?.tools?.common,
      },
      scenarios: [
        {
          type: qualifier,
          opening: conversationFlow.value.has_welcome_message ? openingPhrases : null,
          closing: conversationFlow.value.has_wrap_up_message ? closingPhrases : null,
          contextual_prompt:
            contextualPrompt ?? payload.value.general_information.agent_description,
          ...(isNotBlank(personaUuid)
            ? {
                entry_point_persona: personaUuid,
                personas: {
                  [personaUuid]: {
                    identifier: personaUuid,
                    voice_ids:
                      personaVoiceIds ??
                      resolveVoiceIds(payload.value.general_information.voice_ids),
                    ...(isNotBlank(personaRole) && { role: personaRole }),
                    ...(personaRunToolsOnEnter && { run_tools_on_enter: personaRunToolsOnEnter }),
                  },
                },
              }
            : {
                personas: {
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  [experimental.value.tools?.step_validation ? 'Validator' : 'Qualifier']: {
                    identifier: defaultPersonaKey,
                    voice_ids: resolveVoiceIds(payload.value.general_information.voice_ids),
                  },
                },
              }),
          metadata: {
            customer_intents: payload.value.customer_intents,
          },
        } as DispatchMetadata['scenarios'][0],
      ],
      settings: {
        general_information: payload.value.general_information,
        conversational_flow: {
          has_welcome_message: payload.value.conversational_flow.has_welcome_message,
          has_wrap_up_message: payload.value.conversational_flow.has_wrap_up_message,
        },
        inactivity: payload.value.inactivity,
        call_duration_limits: payload.value.call_duration_limits,
        knowledge: payload.value.knowledge,
        enabled: payload.value.enabled,
        ...(external_tools ? { external_tools } : {}),
        sampling: {
          temperature: payload.value.experimental?.sampling?.temperature,
          top_k: payload.value.experimental?.sampling?.top_k,
          top_p: payload.value.experimental?.sampling?.top_p,
          // features.generation_model is the serialized payload field, not sampling.generation_model (UI reactive)
          ...(payload.value.experimental?.features?.generation_model === GENERATION_MODEL_V2 && {
            max_tokens: payload.value.experimental.sampling?.max_tokens,
          }),
        },
        flags: {
          detection_window: payload.value.experimental?.flags?.detection_window,
          enabled: Object.entries(payload.value.experimental?.flags?.enabled ?? {})
            .filter(([_, value]) => value === true)
            .map(([key]) => key),
        },
        features: {
          ...payload.value.experimental?.features,
          ...(hasVoiceWithCustomModel(payload.value.general_information.voice_ids) && {
            per_voice_tts_model: true,
          }),
        },
        additional_parameters: additionalParams,
        form_schema: (() => {
          const filled = payload.value.experimental?.form_schema?.filter(
            (f) => isNotBlank(f.name) || isNotBlank(f.description),
          )
          return filled && filled.length > 0
            ? filled.map((f) => ({ name: f.name, description: f.description, type: f.type }))
            : undefined
        })(),
        speech: {
          replacements: payload.value.speech?.replacements ?? {},
          language: defaultLang ? defaultLang.code.replace(/[^a-z]/g, '') : 'fr',
          interruptible_welcome: experimentalConfig.speech_interruptions?.welcome ?? true,
          interruptible_wrap_up: experimentalConfig.speech_interruptions?.wrap_up ?? true,
          interruptible_re_engagement:
            experimentalConfig.speech_interruptions?.re_engagement ?? true,
          interruptible_max_duration: experimentalConfig.speech_interruptions?.max_duration ?? true,
        },
      },
    }
  }

  const assignPhoneNumber = async ({
    backendUrl,
    phoneNumber,
    unassign,
  }: {
    backendUrl: string
    phoneNumber: string
    unassign: boolean
  }) => {
    loading.value = true
    try {
      const { getInternalToken } = useQueryParameters()
      const jwtToken = getInternalToken()
      const payload = unassign ? null : getFullDispatchMetadata()
      await assignLivePhoneNumber({
        backendUrl,
        phoneNumber,
        jwtToken,
        payload,
      })
    } catch (err) {
      console.error('[LiveConfigurator] Assignment failed', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    getFullDispatchMetadata,
    assignPhoneNumber,
    loading,
  }
}
